const COLLECTION_VIEWPORT_QUERY_PARAM = 'collection_view';
const COLLECTION_VIEWPORT_BREAKPOINT = '(max-width: 749px)';

class FacetFiltersForm extends HTMLElement {
  constructor() {
    super();
    this.onActiveFilterClick = this.onActiveFilterClick.bind(this);

    this.debouncedOnSubmit = debounce((event) => {
      this.onSubmitHandler(event);
    }, 800);

    const facetForm = this.querySelector('form');
    if (!facetForm) return;
    facetForm.addEventListener('input', this.debouncedOnSubmit.bind(this));

    const facetWrapper = this.querySelector('#FacetsWrapperDesktop');
    if (facetWrapper) facetWrapper.addEventListener('keyup', onKeyUpEscape);
  }

  static setListeners() {
    const onHistoryChange = (event) => {
      const searchParams = event.state ? event.state.searchParams : FacetFiltersForm.searchParamsInitial;
      if (searchParams === FacetFiltersForm.searchParamsPrev) return;
      FacetFiltersForm.renderPage(searchParams, null, false);
    };
    window.addEventListener('popstate', onHistoryChange);
  }

  static toggleActiveFacets(disable = true) {
    document.querySelectorAll('.js-facet-remove').forEach((element) => {
      element.classList.toggle('disabled', disable);
    });
  }

  static renderPage(searchParams, event, updateURLHash = true) {
    searchParams = FacetFiltersForm.normalizeSearchParams(searchParams);
    FacetFiltersForm.searchParamsPrev = searchParams;
    const sections = FacetFiltersForm.getSections();
    if (!sections.length) return;

    const countContainer = document.getElementById('ProductCount');
    const countContainerDesktop = document.getElementById('ProductCountDesktop');
    const loadingSpinners = document.querySelectorAll(
      '.facets-container .loading__spinner, facet-filters-form .loading__spinner'
    );
    loadingSpinners.forEach((spinner) => spinner.classList.remove('hidden'));
    const productGridContainer = document.getElementById('ProductGridContainer');
    const collectionElement = productGridContainer?.querySelector('.collection');
    if (collectionElement) {
      collectionElement.classList.add('loading');
    }
    if (countContainer) {
      countContainer.classList.add('loading');
    }
    if (countContainerDesktop) {
      countContainerDesktop.classList.add('loading');
    }

    sections.forEach((section) => {
      const url = `${window.location.pathname}?section_id=${section.section}&${searchParams}`;
      const filterDataUrl = (element) => element.url === url;

      FacetFiltersForm.filterData.some(filterDataUrl)
        ? FacetFiltersForm.renderSectionFromCache(filterDataUrl, event)
        : FacetFiltersForm.renderSectionFromFetch(url, event);
    });

    if (updateURLHash) FacetFiltersForm.updateURLHash(searchParams);
  }

  static renderSectionFromFetch(url, event) {
    fetch(url)
      .then((response) => response.text())
      .then((responseText) => {
        const html = responseText;
        FacetFiltersForm.filterData = [...FacetFiltersForm.filterData, { html, url }];
        FacetFiltersForm.renderFilters(html, event);
        FacetFiltersForm.renderProductGridContainer(html);
        FacetFiltersForm.renderProductCount(html);
        FacetFiltersForm.initializeProductGridScrollAnimations();
      })
      .catch((error) => {
        console.error('[FacetFilters] Failed to fetch section', error);
        FacetFiltersForm.clearLoadingState();
      });
  }

  static renderSectionFromCache(filterDataUrl, event) {
    const html = FacetFiltersForm.filterData.find(filterDataUrl).html;
    FacetFiltersForm.renderFilters(html, event);
    FacetFiltersForm.renderProductGridContainer(html);
    FacetFiltersForm.renderProductCount(html);
    FacetFiltersForm.initializeProductGridScrollAnimations();
  }

  static initializeProductGridScrollAnimations() {
    const productGridContainer = document.getElementById('ProductGridContainer');
    if (typeof initializeScrollAnimationTrigger !== 'function' || !productGridContainer) return;

    initializeScrollAnimationTrigger(productGridContainer);
  }

  static renderProductGridContainer(html) {
    const parsed = new DOMParser().parseFromString(html, 'text/html');
    const targetContainer = document.getElementById('ProductGridContainer');
    const sourceContainer = parsed.getElementById('ProductGridContainer');
    if (!targetContainer || !sourceContainer) {
      FacetFiltersForm.clearLoadingState();
      return;
    }

    targetContainer.innerHTML = sourceContainer.innerHTML;

    targetContainer.querySelectorAll('.scroll-trigger').forEach((element) => {
      element.classList.add('scroll-trigger--cancel');
    });

    FacetFiltersForm.updateCollectionViewportLinks(targetContainer);

    if (window.initializeCollectionColumns) {
      window.initializeCollectionColumns();
    }
  }

  static renderProductCount(html) {
    const parsed = new DOMParser().parseFromString(html, 'text/html');
    const countElement = parsed.getElementById('ProductCount');
    if (!countElement) {
      FacetFiltersForm.clearLoadingState();
      return;
    }

    const count = countElement.innerHTML;
    const container = document.getElementById('ProductCount');
    const containerDesktop = document.getElementById('ProductCountDesktop');
    if (container) {
      container.innerHTML = count;
      container.classList.remove('loading');
    }
    if (containerDesktop) {
      containerDesktop.innerHTML = count;
      containerDesktop.classList.remove('loading');
    }
    const loadingSpinners = document.querySelectorAll(
      '.facets-container .loading__spinner, facet-filters-form .loading__spinner'
    );
    loadingSpinners.forEach((spinner) => spinner.classList.add('hidden'));
    FacetFiltersForm.collectionViewportSyncInProgress = false;
  }

  static renderFilters(html, event) {
    const parsedHTML = new DOMParser().parseFromString(html, 'text/html');
    const facetDetailsElementsFromFetch = parsedHTML.querySelectorAll(
      '#FacetFiltersForm .js-filter, #FacetFiltersFormMobile .js-filter, #FacetFiltersPillsForm .js-filter'
    );
    const facetDetailsElementsFromDom = document.querySelectorAll(
      '#FacetFiltersForm .js-filter, #FacetFiltersFormMobile .js-filter, #FacetFiltersPillsForm .js-filter'
    );

    // Remove facets that are no longer returned from the server
    Array.from(facetDetailsElementsFromDom).forEach((currentElement) => {
      if (!Array.from(facetDetailsElementsFromFetch).some(({ id }) => currentElement.id === id)) {
        currentElement.remove();
      }
    });

    const matchesId = (element) => {
      const jsFilter = event ? event.target.closest('.js-filter') : undefined;
      return jsFilter ? element.id === jsFilter.id : false;
    };

    const facetsToRender = Array.from(facetDetailsElementsFromFetch).filter((element) => !matchesId(element));
    const countsToRender = Array.from(facetDetailsElementsFromFetch).find(matchesId);

    facetsToRender.forEach((elementToRender, index) => {
      const currentElement = document.getElementById(elementToRender.id);
      // Element already rendered in the DOM so just update the innerHTML
      if (currentElement) {
        document.getElementById(elementToRender.id).innerHTML = elementToRender.innerHTML;
      } else {
        if (index > 0) {
          const { className: previousElementClassName, id: previousElementId } = facetsToRender[index - 1];
          // Same facet type (eg horizontal/vertical or drawer/mobile)
          if (elementToRender.className === previousElementClassName) {
            document.getElementById(previousElementId).after(elementToRender);
            return;
          }
        }

        if (elementToRender.parentElement) {
          const parentFilter = document.querySelector(`#${elementToRender.parentElement.id} .js-filter`);
          if (parentFilter) {
            parentFilter.before(elementToRender);
          }
        }
      }
    });

    FacetFiltersForm.renderActiveFacets(parsedHTML);
    FacetFiltersForm.renderAdditionalElements(parsedHTML);

    if (countsToRender) {
      const closestJSFilterID = event.target.closest('.js-filter').id;

      if (closestJSFilterID) {
        FacetFiltersForm.renderCounts(countsToRender, event.target.closest('.js-filter'));
        FacetFiltersForm.renderMobileCounts(countsToRender, document.getElementById(closestJSFilterID));

        const newFacetDetailsElement = document.getElementById(closestJSFilterID);
        const newElementSelector = newFacetDetailsElement.classList.contains('mobile-facets__details')
          ? `.mobile-facets__close-button`
          : `.facets__summary`;
        const newElementToActivate = newFacetDetailsElement.querySelector(newElementSelector);

        const isTextInput = event.target.getAttribute('type') === 'text';

        if (newElementToActivate && !isTextInput) newElementToActivate.focus();
      }
    }
  }

  static renderActiveFacets(html) {
    const activeFacetElementSelectors = ['.active-facets-mobile', '.active-facets-desktop'];

    activeFacetElementSelectors.forEach((selector) => {
      const activeFacetsElement = html.querySelector(selector);
      if (!activeFacetsElement) return;
      const targetElement = document.querySelector(selector);
      if (!targetElement) return;
      targetElement.innerHTML = activeFacetsElement.innerHTML;
    });

    FacetFiltersForm.toggleActiveFacets(false);
  }

  static renderAdditionalElements(html) {
    const mobileElementSelectors = ['.mobile-facets__open', '.mobile-facets__count', '.sorting'];

    mobileElementSelectors.forEach((selector) => {
      if (!html.querySelector(selector)) return;
      const targetElement = document.querySelector(selector);
      if (!targetElement) return;
      targetElement.innerHTML = html.querySelector(selector).innerHTML;
    });

    const facetFiltersFormMobile = document.getElementById('FacetFiltersFormMobile');
    facetFiltersFormMobile?.closest('menu-drawer')?.bindEvents();
  }

  static renderCounts(source, target) {
    const targetSummary = target.querySelector('.facets__summary');
    const sourceSummary = source.querySelector('.facets__summary');

    if (sourceSummary && targetSummary) {
      targetSummary.outerHTML = sourceSummary.outerHTML;
    }

    const targetHeaderElement = target.querySelector('.facets__header');
    const sourceHeaderElement = source.querySelector('.facets__header');

    if (sourceHeaderElement && targetHeaderElement) {
      targetHeaderElement.outerHTML = sourceHeaderElement.outerHTML;
    }

    const targetWrapElement = target.querySelector('.facets-wrap');
    const sourceWrapElement = source.querySelector('.facets-wrap');

    if (sourceWrapElement && targetWrapElement) {
      const isShowingMore = Boolean(target.querySelector('show-more-button .label-show-more.hidden'));
      if (isShowingMore) {
        sourceWrapElement
          .querySelectorAll('.facets__item.hidden')
          .forEach((hiddenItem) => hiddenItem.classList.replace('hidden', 'show-more-item'));
      }

      targetWrapElement.outerHTML = sourceWrapElement.outerHTML;
    }
  }

  static renderMobileCounts(source, target) {
    const targetFacetsList = target.querySelector('.mobile-facets__list');
    const sourceFacetsList = source.querySelector('.mobile-facets__list');

    if (sourceFacetsList && targetFacetsList) {
      targetFacetsList.outerHTML = sourceFacetsList.outerHTML;
    }
  }

  static updateURLHash(searchParams) {
    history.pushState({ searchParams }, '', `${window.location.pathname}${searchParams && '?'.concat(searchParams)}`);
  }

  static getCollectionViewportState(root = document) {
    const productGrid = root.querySelector ? root.querySelector('#product-grid') : document.getElementById('product-grid');
    if (!productGrid?.dataset?.collectionPageSizeMode) return null;

    return {
      activeProductsPerPage: Number(productGrid.dataset.activeProductsPerPage) || 0,
      activeViewport: productGrid.dataset.activeViewport || 'desktop',
      currentPage: Number(productGrid.dataset.currentPage) || 1,
      desktopProductsPerPage: Number(productGrid.dataset.desktopProductsPerPage) || 0,
      mobileProductsPerPage: Number(productGrid.dataset.mobileProductsPerPage) || 0,
      productGrid,
      queryParam: productGrid.dataset.pageSizeQueryParam || COLLECTION_VIEWPORT_QUERY_PARAM,
    };
  }

  static getDesiredCollectionViewport() {
    return window.matchMedia(COLLECTION_VIEWPORT_BREAKPOINT).matches ? 'mobile' : 'desktop';
  }

  static normalizeSearchParams(searchParams = '') {
    const collectionViewportState = FacetFiltersForm.getCollectionViewportState();
    if (!collectionViewportState) return searchParams;

    const params = new URLSearchParams(searchParams);
    params.set(collectionViewportState.queryParam, FacetFiltersForm.getDesiredCollectionViewport());
    return params.toString();
  }

  static updateCollectionViewportLinks(root = document) {
    const collectionViewportState = FacetFiltersForm.getCollectionViewportState(root);
    if (!collectionViewportState) return;

    const desiredViewport = FacetFiltersForm.getDesiredCollectionViewport();

    root.querySelectorAll('.pagination a[href]').forEach((link) => {
      const url = new URL(link.getAttribute('href'), window.location.origin);
      url.searchParams.set(collectionViewportState.queryParam, desiredViewport);
      link.href = `${url.pathname}${url.search}${url.hash}`;
    });
  }

  static syncCollectionViewportPageSize() {
    const collectionViewportState = FacetFiltersForm.getCollectionViewportState();
    if (!collectionViewportState || FacetFiltersForm.collectionViewportSyncInProgress) {
      FacetFiltersForm.updateCollectionViewportLinks();
      return;
    }

    const desiredViewport = FacetFiltersForm.getDesiredCollectionViewport();
    const currentSearchParams = new URLSearchParams(window.location.search);
    const currentViewportParam = currentSearchParams.get(collectionViewportState.queryParam);
    const requiresSync =
      collectionViewportState.activeViewport !== desiredViewport ||
      (currentViewportParam && currentViewportParam !== desiredViewport);

    if (!requiresSync) {
      FacetFiltersForm.updateCollectionViewportLinks();
      return;
    }

    const activeProductsPerPage =
      collectionViewportState.activeProductsPerPage ||
      (collectionViewportState.activeViewport === 'mobile'
        ? collectionViewportState.mobileProductsPerPage
        : collectionViewportState.desktopProductsPerPage);
    const desiredProductsPerPage =
      desiredViewport === 'mobile'
        ? collectionViewportState.mobileProductsPerPage
        : collectionViewportState.desktopProductsPerPage;
    const firstVisibleProductIndex = Math.max(
      1,
      (collectionViewportState.currentPage - 1) * activeProductsPerPage + 1
    );
    const nextPage =
      activeProductsPerPage && desiredProductsPerPage
        ? Math.max(1, Math.ceil(firstVisibleProductIndex / desiredProductsPerPage))
        : collectionViewportState.currentPage;

    currentSearchParams.set(collectionViewportState.queryParam, desiredViewport);
    if (nextPage > 1) {
      currentSearchParams.set('page', nextPage);
    } else {
      currentSearchParams.delete('page');
    }

    FacetFiltersForm.collectionViewportSyncInProgress = true;
    FacetFiltersForm.renderPage(currentSearchParams.toString(), null, true);
  }

  static recoverOutOfRangeCollectionPage() {
    const collectionViewportState = FacetFiltersForm.getCollectionViewportState();
    const productGrid = collectionViewportState?.productGrid;
    if (!productGrid || FacetFiltersForm.collectionViewportSyncInProgress) {
      return false;
    }

    const currentSearchParams = new URLSearchParams(window.location.search);

    if (productGrid.dataset.emptyFilteredPage) {
      const staleFilterParams = Array.from(currentSearchParams.keys()).filter((key) => key.startsWith('filter.'));
      if (!staleFilterParams.length) return false;

      staleFilterParams.forEach((key) => currentSearchParams.delete(key));
      currentSearchParams.delete('page');
    } else {
      if (!productGrid.dataset.emptyOutOfRangePage) return false;

      if (currentSearchParams.has('page')) {
        currentSearchParams.delete('page');
      } else if (currentSearchParams.toString() === '') {
        return false;
      } else {
        currentSearchParams.delete(collectionViewportState.queryParam);
      }
    }

    FacetFiltersForm.collectionViewportSyncInProgress = true;
    FacetFiltersForm.renderPage(currentSearchParams.toString(), null, true);
    return true;
  }

  static initializeCollectionViewportSync() {
    FacetFiltersForm.updateCollectionViewportLinks();

    if (FacetFiltersForm.collectionViewportMediaQuery) return;

    const collectionViewportState = FacetFiltersForm.getCollectionViewportState();
    if (!collectionViewportState) return;
    if (FacetFiltersForm.recoverOutOfRangeCollectionPage()) return;

    const mediaQuery = window.matchMedia(COLLECTION_VIEWPORT_BREAKPOINT);
    const handleCollectionViewportChange = () => {
      FacetFiltersForm.syncCollectionViewportPageSize();
    };

    if (mediaQuery.addEventListener) {
      mediaQuery.addEventListener('change', handleCollectionViewportChange);
    } else if (mediaQuery.addListener) {
      mediaQuery.addListener(handleCollectionViewportChange);
    }

    FacetFiltersForm.collectionViewportMediaQuery = mediaQuery;
    FacetFiltersForm.syncCollectionViewportPageSize();
  }

  static getSections() {
    const productGrid = document.getElementById('product-grid');
    const sectionId = productGrid?.dataset?.id;
    if (!sectionId) return [];

    return [
      {
        section: sectionId,
      },
    ];
  }

  static clearLoadingState() {
    FacetFiltersForm.collectionViewportSyncInProgress = false;

    const countContainer = document.getElementById('ProductCount');
    const countContainerDesktop = document.getElementById('ProductCountDesktop');
    const productGridContainer = document.getElementById('ProductGridContainer');
    const collectionElement = productGridContainer?.querySelector('.collection');

    if (collectionElement) {
      collectionElement.classList.remove('loading');
    }
    if (countContainer) {
      countContainer.classList.remove('loading');
    }
    if (countContainerDesktop) {
      countContainerDesktop.classList.remove('loading');
    }

    const loadingSpinners = document.querySelectorAll(
      '.facets-container .loading__spinner, facet-filters-form .loading__spinner'
    );
    loadingSpinners.forEach((spinner) => spinner.classList.add('hidden'));
  }

  createSearchParams(form) {
    const formData = new FormData(form);
    return new URLSearchParams(formData).toString();
  }

  onSubmitForm(searchParams, event) {
    FacetFiltersForm.renderPage(searchParams, event);
  }

  onSubmitHandler(event) {
    event.preventDefault();
    const sortFilterForms = document.querySelectorAll('facet-filters-form form');
    if (event.srcElement.className == 'mobile-facets__checkbox') {
      const searchParams = this.createSearchParams(event.target.closest('form'));
      this.onSubmitForm(searchParams, event);
    } else {
      const forms = [];
      const isMobile = event.target.closest('form').id === 'FacetFiltersFormMobile';

      sortFilterForms.forEach((form) => {
        if (!isMobile) {
          if (form.id === 'FacetSortForm' || form.id === 'FacetFiltersForm' || form.id === 'FacetSortDrawerForm') {
            forms.push(this.createSearchParams(form));
          }
        } else if (form.id === 'FacetFiltersFormMobile') {
          forms.push(this.createSearchParams(form));
        }
      });
      this.onSubmitForm(forms.join('&'), event);
    }
  }

  onActiveFilterClick(event) {
    event.preventDefault();
    FacetFiltersForm.toggleActiveFacets();
    const url = new URL(event.currentTarget.href, window.location.origin);
    url.searchParams.delete('page');
    FacetFiltersForm.renderPage(url.searchParams.toString());
  }
}

FacetFiltersForm.filterData = [];
FacetFiltersForm.collectionViewportMediaQuery = null;
FacetFiltersForm.collectionViewportSyncInProgress = false;
FacetFiltersForm.searchParamsInitial = window.location.search.slice(1);
FacetFiltersForm.searchParamsPrev = window.location.search.slice(1);
customElements.define('facet-filters-form', FacetFiltersForm);
FacetFiltersForm.setListeners();
FacetFiltersForm.initializeCollectionViewportSync();

class PriceRange extends HTMLElement {
  constructor() {
    super();
    this.querySelectorAll('input').forEach((element) => {
      element.addEventListener('change', this.onRangeChange.bind(this));
      element.addEventListener('keydown', this.onKeyDown.bind(this));
    });
    this.setMinAndMaxValues();
  }

  onRangeChange(event) {
    this.adjustToValidValues(event.currentTarget);
    this.setMinAndMaxValues();
  }

  onKeyDown(event) {
    if (event.metaKey) return;

    const pattern = /[0-9]|\.|,|'| |Tab|Backspace|Enter|ArrowUp|ArrowDown|ArrowLeft|ArrowRight|Delete|Escape/;
    if (!event.key.match(pattern)) event.preventDefault();
  }

  setMinAndMaxValues() {
    const inputs = this.querySelectorAll('input');
    const minInput = inputs[0];
    const maxInput = inputs[1];
    if (maxInput.value) minInput.setAttribute('data-max', maxInput.value);
    if (minInput.value) maxInput.setAttribute('data-min', minInput.value);
    if (minInput.value === '') maxInput.setAttribute('data-min', 0);
    if (maxInput.value === '') minInput.setAttribute('data-max', maxInput.getAttribute('data-max'));
  }

  adjustToValidValues(input) {
    const value = Number(input.value);
    const min = Number(input.getAttribute('data-min'));
    const max = Number(input.getAttribute('data-max'));

    if (value < min) input.value = min;
    if (value > max) input.value = max;
  }
}

customElements.define('price-range', PriceRange);

class FacetRemove extends HTMLElement {
  constructor() {
    super();
    const facetLink = this.querySelector('a');
    facetLink.setAttribute('role', 'button');
    facetLink.addEventListener('click', this.closeFilter.bind(this));
    facetLink.addEventListener('keyup', (event) => {
      event.preventDefault();
      if (event.code.toUpperCase() === 'SPACE') this.closeFilter(event);
    });
  }

  closeFilter(event) {
    event.preventDefault();
    const form = this.closest('facet-filters-form') || document.querySelector('facet-filters-form');
    form.onActiveFilterClick(event);
  }
}

customElements.define('facet-remove', FacetRemove);
